import React, { useState } from 'react'
import { ApolloProvider, useQuery } from '@apollo/client/react'
import { CustomFormats, IntlProvider } from 'react-intl'
import { BrowserRouter } from 'react-router-dom'
import { App } from './App'
import { createGraphqlClient, IS_AUTHENTICATED_QUERY } from '../../graphql-client/graphql-client'
import fiMessages from '../../translations/fi.json'
import svMessages from '../../translations/sv.json'
import enMessages from '../../translations/en.json'
import { StoreProvider } from '../StoreProvider'
import { handleEnvironmentSettings } from '../../util/environment'
import { SocketContext, socket } from '../SocketProvider'
import { useLoadScripts } from '../../util/useGoogle'
import { GlobalStyle } from '../../styled'
import { ErrorFallback } from './ErrorFallback'
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import { ErrorBoundary } from '../../components/ErrorBoundary'
import { Locale } from '../../types/locale'
import { LocaleContext, getStoredLocale, setStoredLocale } from '../../util/locale'

// Override methods for different environments
handleEnvironmentSettings()

const messagesByLocale: Record<Locale, Record<string, string>> = {
  'fi-FI': fiMessages,
  'sv-SE': svMessages,
  'en-US': enMessages,
}

interface QueryData {
  authenticated: boolean
}

const apolloClient = createGraphqlClient()

if (process.env.NODE_ENV === 'development' ) {
    loadDevMessages();
    loadErrorMessages();
}
// react-intl v5+ formats
const dateAndTimeFormats: CustomFormats = {
  date: {
    full: {
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    },
  },
}

const AppWithAuth: React.FC = () => {
  const { data } = useQuery<QueryData>(IS_AUTHENTICATED_QUERY)
  const authenticated = data?.authenticated ?? false

  return (
    <SocketContext.Provider value={socket}>
      <StoreProvider>
        <BrowserRouter>
          <GlobalStyle />
          <App authenticated={authenticated} />
        </BrowserRouter>
      </StoreProvider>
    </SocketContext.Provider>
  )
}

export const AppContainer: React.FC = () => {
  useLoadScripts()
  const [locale, setLocaleState] = useState<Locale>(getStoredLocale)

  const setLocale = (newLocale: Locale) => {
    setStoredLocale(newLocale)
    setLocaleState(newLocale)
  }

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <LocaleContext.Provider value={{ locale, setLocale }}>
        <IntlProvider locale={locale} defaultLocale="fi-FI" messages={messagesByLocale[locale]} formats={dateAndTimeFormats}>
          <ApolloProvider client={apolloClient}>
            <AppWithAuth />
          </ApolloProvider>
        </IntlProvider>
      </LocaleContext.Provider>
    </ErrorBoundary>
  )
}

